import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ProfileCompletion, StudentProfileFields } from '../../utils/profileCompletion';
import MainLogo from '../Titles/MainLogo';
import Subtitle from '../Texts/Subtitle';
import TextInput from '../Inputs/TextInput';
import OutlineButton from '../Buttons/OutlineButton';

type CompleteGoogleProfileModalProps = {
  profile: ProfileCompletion | null;
  onComplete: (fields: StudentProfileFields) => Promise<void>;
};

const CompleteGoogleProfileModal: React.FC<CompleteGoogleProfileModalProps> = ({
  profile,
  onComplete,
}) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setFirstName(profile.firstName);
    setLastName(profile.lastName);
    setPhone(profile.phone);
    setCity(profile.city);
    setPostalCode(profile.postalCode);
    setError('');
  }, [profile]);

  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    phone.trim().length >= 7 &&
    city.trim().length >= 2 &&
    postalCode.trim().length >= 3;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSaving(true);

    try {
      await onComplete({
        firstName,
        lastName,
        phone,
        city,
        postalCode,
      });
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : t('auth.profileCompletionSaveFailed'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={Boolean(profile)}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      aria-labelledby="complete-profile-title"
      slotProps={{
        paper: {
          sx: {
            border: '5px solid',
            borderColor: 'primary.light',
            borderRadius: '20px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2), 0 6px 20px rgba(0, 0, 0, 0.19)',
            overflow: 'hidden',
          },
        },
        backdrop: {
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(2px)',
          },
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2, sm: 3.5 }, textAlign: 'center', bgcolor: 'common.white' }}>
        <MainLogo />
        <Subtitle id="complete-profile-title">
          {t('auth.profileCompletionTitle')}
        </Subtitle>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 2 }}>
          {t('auth.profileCompletionDescription')}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <TextInput
            label={t('auth.firstName')}
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            autoComplete="given-name"
            required
            fullWidth
            margin="normal"
            inputProps={{ maxLength: 50 }}
          />
          <TextInput
            label={t('auth.lastName')}
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            autoComplete="family-name"
            required
            fullWidth
            margin="normal"
            inputProps={{ maxLength: 50 }}
          />
          <TextInput
            label={t('auth.phone')}
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            autoComplete="tel"
            required
            fullWidth
            margin="normal"
            inputProps={{ minLength: 7, maxLength: 20 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextInput
                label={t('auth.city')}
                value={city}
                onChange={(event) => setCity(event.target.value)}
                autoComplete="address-level2"
                required
                fullWidth
                margin="normal"
                inputProps={{ minLength: 2, maxLength: 50 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                label={t('auth.postalCode')}
                value={postalCode}
                onChange={(event) => setPostalCode(event.target.value)}
                autoComplete="postal-code"
                required
                fullWidth
                margin="normal"
                inputProps={{ minLength: 3, maxLength: 10 }}
              />
            </Grid>
          </Grid>
          <OutlineButton type="submit" disabled={saving || !canSubmit}>
            {saving ? <CircularProgress size={24} color="inherit" /> : t('auth.profileCompletionContinue')}
          </OutlineButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CompleteGoogleProfileModal;
